import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { GetServerSideProps } from 'next'
import type { AppProps } from 'next/app'
import OneSignal from 'react-onesignal'
import { useAvatarStore } from '@/store/zustand'
import Script from 'next/script'
import { Analytics } from '@vercel/analytics/next'
import { SpeedInsights } from '@vercel/speed-insights/next'
import { Constants } from '@/assets'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, Stripe, StripeElementLocale } from '@stripe/stripe-js'
import { FBPixelProvider, FBPixelScript } from '@rivercode/facebook-conversion-api-nextjs/components'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { generateTheme } from 'emt-components-v2'
import { AppProvider, useEMTDispatch, useEMTState } from '@/store'
import { ConfigurationServiceHelper } from '@/modules/Configuration'
import NotificationsView from '@/components/NotificationsView'
import DisconnectionModal from '@/components/DisconnectionModal'
import AuthApiSingleton from '@/modules/Auth/AuthApi'
import Head from 'next/head'
import { computeMessageEventData_Login } from '@/data'
import { UserServiceHelper } from '@/modules/User'
import { PetsitterUserServiceHelper } from '@/modules/PetsitterUser'
import { useFirstRouterQueryParam, useMercureES } from '@/hooks'
import { GTMScript, useGTMPageView } from '@/helpers/GTMHelper'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import palette from '@/assets/themes/palettes/palette'
import '@/assets/css/global.css'
import '@/assets/css/slick.css'
import '../../public/fonts/empruntemontoutou-webfont.css'
import '../../public/fonts/icons/emticons.css'
import GlobalLoader from '@/components/GlobalLoader'
import WelcomeLoader from '@/components/WelcomeLoader'
import { checkImageLoaded } from '@/helpers/checkImageLoaded'
import { ModalAddAvatarImage } from '@/components/IsThereAvatarModal'
import { Routes, SpecificModules } from '@/assets/configs'
import { useRouter } from 'next/router'
import { getStripeApiKey } from '@/modules/Stripe/StripeServiceHelper'
import useCurrentUser from '@/hooks/useCurrentUser'
import { dispatchLoadCartFromAPI } from '@/modules/Cart/CartServiceHelper'
import { AuthServiceHelper } from '@/modules/Auth'
// import { dispatchAddNotification } from '@/modules/ApiNotifications/ApiNotificationsServiceHelper'
// import { DiscussionsTypes } from '@/modules/Discussions'
import { useUIStore } from '@/store/ui/uiStore'
import getEMTServerSideProps from '@/helpers/getEMTServerSideProps'
import { MessagesMercureProvider } from '@/modules/PetSitter/MessagesMercureProvider'
import PetSitterTransitionModal from '@/components/pages/pet-sitter/pro/PetSitterTransitionModal'
import PetOwnerTransitionModal from '@/components/pages/pet-sitter/pro/PetOwnerTransitionModal'
import AppInstallBanner from '@/components/AppInstallBanner'

function MyApp({ Component, pageProps }: AppProps & { Component: any }) {
  const { isPetsittingMode, setPetsittingMode } = usePetsittingMode()
  const { isThereAvatar, setIsThereAvatar, setActiveAvatarModal } = useAvatarStore()
  const { disconnectionModalOpen } = useUIStore()
  const [petOwnerTransitionModalOpen, setPetOwnerTransitionModalOpen] = useState(true)

  const { i18n } = useTranslation()
  const tc = useTranslation('common').t

  const [isLoading, setLoading] = useState<boolean>(false)
  const [stripePromise, setStripePromise] = useState<Stripe | null>(null)
  const [stripeIsLoad, setStripeIsLoad] = useState<boolean>(false)
  const hasReconnectedRef = useRef(false)

  useEffect(() => {
    const stored = localStorage.getItem('petsitting-mode-storage')

    if (!stored) {
      const initial = Constants?.petsitting === 'true' || Constants?.petsitting === true
      setPetsittingMode(initial)
    }
  }, [])

  // Create memoized theme
  const theme = useMemo(() => {
    return generateTheme(palette)
  }, [])

  // Get App Dispatch & State
  const dispatch = useEMTDispatch()
  const { isPremium, avatar, settings, currentUser, isPetSitter, hasRequestedPetsitting, currentPetsitter } =
    useCurrentUser()
  const { currentPrices = [] } = useEMTState().user
  const { currentApiNotifications } = useEMTState().apiNotifications
  const router = useRouter()
  const [type] = useFirstRouterQueryParam(['type'])

  useGTMPageView(pageProps?.title)

  const refreshConfigurationAsync = async () => {
    try {
      setLoading(true)
      await ConfigurationServiceHelper.dispatchRefreshAllConfigurationAsync(dispatch)()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (stripeIsLoad) return

    const initAsync = async () => {
      // Load Stripe
      if (currentUser) {
        try {
          const stripeKey = await getStripeApiKey()
          const stripe = await loadStripe(stripeKey)
          setStripePromise(stripe)
          setStripeIsLoad(true)
        } catch (err) {
          console.warn('Cannot initialize Stripe api', err)
        }
      }
    }

    initAsync().finally()
  }, [currentUser])

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Constants', Constants)
    }

    UserServiceHelper.dispatchGetShadowOptinsAsync(dispatch)()
    AuthApiSingleton.setLocale(i18n.resolvedLanguage as string)

    // Refresh all configurations at app startup
    refreshConfigurationAsync()
  }, [])

  useEffect(() => {
    // Check if user has avatar
    if (avatar) {
      checkImageLoaded(avatar.url, setIsThereAvatar)
    }
  }, [avatar])

  // active / deactive avatar modal if there is avatar
  useEffect(() => {
    setActiveAvatarModal(!isThereAvatar)
  }, [isThereAvatar])

  useEffect(() => {
    if (currentUser) {
      OneSignal.init({
        appId: Constants.onesignalAppId,
        notifyButton: {
          enable: false,
        },
        allowLocalhostAsSecureOrigin: true,
        userId: currentUser.id,
      }).then(() => {
        OneSignal.setExternalUserId(currentUser.id)
        OneSignal.sendTag('userId', currentUser.id)
        OneSignal.showSlidedownPrompt()
      })
    }
  }, [currentUser?.id])

  // ApiNotifications
  useEffect(() => {
    if (currentUser && !currentApiNotifications?.length) {
      UserServiceHelper.dispatchNotificationsAsync(dispatch)(currentUser.id).catch((err) =>
        console.warn('Cannot get Notifications', err),
      )
    }
  }, [currentUser?.id])

  // Refresh prices only if user is connected
  useEffect(() => {
    if (currentUser && !currentPrices.length) {
      UserServiceHelper.dispatchGetPrices(dispatch)().catch((err) => console.warn('Cannot get Prices', err))
    }
  }, [currentUser?.id])

  // Load cart when app initializes or user changes
  useEffect(() => {
    if (currentUser) {
      // Error is catched inside function call
      dispatchLoadCartFromAPI(dispatch)()
    }
  }, [currentUser?.id])

  // React query
  const [queryClient] = useState(() => new QueryClient())

  // 🔄 MERCURE - Refresh
  const onCustomerRefreshMessage = useCallback(
    (/* data: { premium: boolean } */) => {
      UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()
      if (isPetSitter) {
        PetsitterUserServiceHelper.dispatchGetPetsitterInfoAsync(dispatch)()
      }
    },
    [isPetSitter],
  )

  // 🔄 MERCURE – Customer refresh (@refreshTopic = full IRI)
  useMercureES(
    currentUser?.['@refreshTopic'] ?? null,
    onCustomerRefreshMessage,
    (err) => console.warn('[Mercure] customer refresh error:', err?.message),
    () => console.debug('[Mercure] customer refresh open'),
    { completeTopicPath: true },
  )

  //   dispatch({ type: DiscussionsTypes.UnreadCountUpdated, payload: unreadMessages })
  // }, [])

  // const onStateEventError = useCallback((error?: Error) => {
  //   console.log('SSE message Error: ', error?.message)
  // }, [])

  // const onStateEventOpen = useCallback(() => {
  //   // Silence Log
  // }, [])

  // // Optional states stream (relative)
  // useMercureES(
  //   currentUser?.id ? `customer/${currentUser.id}/states` : null,
  //   onStateEventReceived,
  //   onStateEventError,
  //   onStateEventOpen,
  // )

  const onPetsitterRefreshMessage = useCallback(async () => {
    await PetsitterUserServiceHelper.dispatchGetPetsitterInfoAsync(dispatch)()
  }, [])

  // 🔄 MERCURE – Petsitter refresh
  useMercureES(
    currentPetsitter?.id ? `petsitting/account/${currentPetsitter.id}` : null,
    onPetsitterRefreshMessage,
    (err) => console.warn('[Mercure] petsitter refresh error:', err?.message),
    () => console.debug('[Mercure] petsitter refresh open'),
    { completeTopicPath: true },
  )

  // 🔄 MERCURE - Banishment
  const onBanishmentEventReceived = useCallback(async () => {
    await AuthServiceHelper.dispatchLogoutAsync(dispatch)()
  }, [])

  // 🔄 MERCURE - Notifications
  // const onApiNotificationReceived = useCallback(
  //   async (data: ApiNotification) => dispatchAddNotification(dispatch)(data),
  //   [],
  // )

  // API notifications (relative) – example left commented
  // useMercureES(
  //   currentUser?.id ? `customers/${currentUser.id}/notifications` : null,
  //   onApiNotificationReceived,
  //   (err?: Error) => console.log('SSE notifications error:', err?.message),
  // )

  // Banishment stream (literal “/members/…” topic)
  useMercureES(
    currentUser?.id ? `/members/account/${currentUser?.id}/bann` : null,
    onBanishmentEventReceived,
    (err) => console.warn('[Mercure] banishment error:', err?.message),
    () => console.debug('[Mercure] banishment open'),
    { completeTopicPath: true },
  )

  // Feed store
  useEffect(() => {
    if (currentUser) {
      UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()
      if (isPetSitter) {
        PetsitterUserServiceHelper.dispatchGetPetsitterInfoAsync(dispatch)()
      }
    }
    if (currentPetsitter) {
      PetsitterUserServiceHelper.dispatchGetPetsitterInfoAsync(dispatch)()
    }
  }, [isPetSitter])

  // Change color palette with router
  if (
    isPetsittingMode ||
    router.pathname === '/pro' ||
    router.pathname.startsWith('/pro/') ||
    type === 'pro' ||
    router.pathname.startsWith('/sign-up-call-request') ||
    router.pathname.startsWith('/testimony')
  ) {
    theme.palette.primary = (theme.palette as any).purple
  } else {
    theme.palette.primary = theme.palette.red
  }
  // Webview tracking
  useEffect(() => {
    // Only run if we haven't posted before
    if (hasReconnectedRef.current) return

    // Make sure we have a user and the RN webview is available
    if (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined' && currentUser) {
      const eventData = computeMessageEventData_Login('reconnect', currentUser)

      // Post the message only once
      window.ReactNativeWebView.postMessage(JSON.stringify(eventData))
      hasReconnectedRef.current = true
    }
  }, [currentUser, settings, isPremium])

  const isPetsittingRoute = router.pathname === '/pet-sitter' || router.pathname.startsWith('/pet-sitter/')

  const isSeoPublicRoute =
    router.pathname.startsWith('/view-public-profile-petsitter/') || router.pathname.startsWith('/view-public-profile/')

  if (isSeoPublicRoute) {
    return (
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <CssBaseline />
          <NotificationsView />
          <Component {...pageProps} />
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      {stripePromise && stripeIsLoad ? (
        <Elements stripe={stripePromise} options={{ locale: i18n.language as StripeElementLocale }}>
          <CssBaseline />
          <NotificationsView />
          {isLoading && !isSeoPublicRoute ? (
            router.pathname !== Routes.Connection.Welcome ? (
              <GlobalLoader />
            ) : (
              <WelcomeLoader />
            )
          ) : (
            <QueryClientProvider client={queryClient}>
              {process.env.NODE_ENV === 'development' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
              <MessagesMercureProvider>
                <AppInstallBanner
                  enabled={true}
                  dismissDays={7}
                  excludedPathPrefixes={['/ghost', '/premium/payment']}
                />
                <Component {...pageProps} />
              </MessagesMercureProvider>
              <Analytics />
              <SpeedInsights />
            </QueryClientProvider>
          )}
        </Elements>
      ) : router.pathname !== '/ghost/paypal' && router.pathname !== '/premium/payment' ? (
        <>
          <CssBaseline />
          <NotificationsView />
          {isLoading && !isSeoPublicRoute ? (
            router.pathname !== Routes.Connection.Welcome ? (
              <GlobalLoader />
            ) : (
              <WelcomeLoader />
            )
          ) : (
            <QueryClientProvider client={queryClient}>
              {process.env.NODE_ENV === 'development' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
              <MessagesMercureProvider>
                <AppInstallBanner
                  enabled={true}
                  dismissDays={7}
                  excludedPathPrefixes={['/ghost', '/premium/payment']}
                />
                <Component {...pageProps} />
              </MessagesMercureProvider>
              <Analytics />
              <SpeedInsights />
            </QueryClientProvider>
          )}
        </>
      ) : router.pathname !== Routes.Connection.Welcome ? (
        <GlobalLoader />
      ) : (
        <WelcomeLoader />
      )}

      {/* Can't delete account Modal */}
      {SpecificModules.missingImages ? <ModalAddAvatarImage t={tc} /> : null}
      {/* Disconnection Modal */}
      {disconnectionModalOpen ? <DisconnectionModal /> : null}
      {/* Transition Super Sitters for Pet sitters */}
      {(isPetSitter || currentPetsitter) && !isPetsittingMode ? (
        <PetSitterTransitionModal open={true} setOpen={() => {}} />
      ) : null}
      {isPetsittingRoute && !isPetsittingMode && !hasRequestedPetsitting ? (
        <PetOwnerTransitionModal open={petOwnerTransitionModalOpen} setOpen={setPetOwnerTransitionModalOpen} />
      ) : null}
    </ThemeProvider>
  )
}

/**
 * Component only used to Wrap with AppProvider (Reducer/Store) because we need dispatch in MyApp
 * @param props
 * @returns
 */
function MyAppWrapper(props: AppProps) {
  const router = useRouter()
  const isSeoPublicRoute =
    router.pathname.startsWith('/view-public-profile-petsitter/') || router.pathname.startsWith('/view-public-profile/')
  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" />
      </Head>

      <GTMScript />

      <FBPixelScript />

      {/* tag supplémentaire pour les conversions sur /adress-photo.tsx à l'inscription */}
      <Script async src="https://www.googletagmanager.com/gtag/js?id=AW-860378517" strategy="afterInteractive" />
      <Script id="google-ads-script" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'AW-860378517');
        `}
      </Script>

      <FBPixelProvider>
        <AppProvider isSeoPublicRoute={isSeoPublicRoute}>
          <MyApp {...props} />
        </AppProvider>
      </FBPixelProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = getEMTServerSideProps({
  namespaces: ['common'],
  i18nTitleNs: 'common',
  i18nTitleKey: 'seo.title',
})

export default appWithTranslation(MyAppWrapper)
